import { useTranslation } from 'react-i18next';
import { Youtube, Facebook, Instagram } from 'lucide-react';

// Custom TikTok icon component
const TikTokIcon = ({ size = 24, className = '' }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 24 24" 
    fill="currentColor" 
    className={className}
  >
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

function PlatformSelector({ 
  platforms, 
  connectedPlatforms, 
  uploadStatus, 
  onTogglePlatform, 
  getStatusIcon,
  facebookPages,
  selectedFacebookPage,
  onSelectFacebookPage,
  facebookUserName,
  youtubeChannel,
  tiktokCreatorInfo
}) {
  const { t } = useTranslation();
  const platformConfig = [
    { id: 'youtube', name: 'YouTube', icon: Youtube, color: 'red' },
    { id: 'facebook', name: 'Facebook', icon: Facebook, color: 'blue' },
    { id: 'instagram', name: 'Instagram', icon: Instagram, color: 'red' },
    { id: 'tiktok', name: 'TikTok', icon: TikTokIcon, color: 'black' }
  ];

  // Define color classes explicitly for Tailwind
  const getColorClasses = (color, isSelected) => {
    if (!isSelected) return '';
    
    const colorMap = {
      red: 'border-red-500 bg-red-100',
      blue: 'border-blue-500 bg-blue-100',
      black: 'border-black bg-gray-100'
    };
    
    return colorMap[color] || '';
  };

  const getIconColorClass = (color, isSelected) => {
    if (!isSelected) return 'text-gray-400';
    
    const iconColorMap = {
      red: 'text-red-500',
      blue: 'text-blue-500',
      black: 'text-black'
    };
    
    return iconColorMap[color] || 'text-gray-400';
  };

  const connectedPlatformConfig = platformConfig.filter(({ id }) => connectedPlatforms[id]);
  return (
    <div className="bg-white rounded-xl shadow-sm p-6">
      <h3 className="text-lg font-bold text-gray-800 mb-4">{t('app.platformSelector.title')}</h3>
      <div className="space-y-3">
        {connectedPlatformConfig.length > 0 ? (
          connectedPlatformConfig.map(({ id, name, icon: Icon, color }) => (
            <div key={id}>
              <div
                onClick={() => onTogglePlatform(id)}
                className={`cursor-pointer p-4 rounded-lg border-2 transition-all ${
                  platforms[id]
                    ? getColorClasses(color, true)
                    : 'border-gray-200 hover:border-gray-400'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <Icon className={getIconColorClass(color, platforms[id])} size={24} />
                    <div className="flex flex-col">
                      <span className="font-medium">{name}</span>
                      {id === 'facebook' && (selectedFacebookPage || facebookUserName) && (
                        <span className="text-xs text-gray-500">
                          {selectedFacebookPage ? selectedFacebookPage.name : facebookUserName}
                        </span>
                      )}
                      {id === 'youtube' && youtubeChannel && (
                        <span className="text-xs text-gray-500">
                          {youtubeChannel.title || youtubeChannel.name || youtubeChannel.snippet?.title || 'YouTube Channel'}
                        </span>
                      )}
                      {id === 'tiktok' && tiktokCreatorInfo && (tiktokCreatorInfo.creator_nickname || tiktokCreatorInfo.creator_username) && (
                        <span className="text-xs text-gray-500">
                          {tiktokCreatorInfo.creator_nickname || `@${tiktokCreatorInfo.creator_username}`}
                        </span>
                      )}
                    </div>
                  </div>
                  {uploadStatus[id] && getStatusIcon(uploadStatus[id])}
                </div>
              </div>
            </div>
          ))
        ) : (
          <span className="text-gray-600 text-center block">{t('app.platformSelector.noConnectedPlatforms')}</span>
        )}
      </div>
    </div>
  );
}

export default PlatformSelector;